/**
 * @file EditEmailConversation.jsx
 * @description EditEmailConversation component for displaying and managing email conversation.
 * @component EditEmailConversation
 * @exports EditEmailConversation - Main component for displaying and managing email conversation.
 */

import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Button,
  Container,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Box,
  TextField,
  IconButton,
  Tabs,
  Tab,
  Alert,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import { Save, Send } from "@mui/icons-material";
import EmailIcon from "@mui/icons-material/Email";
import CampaignIcon from "@mui/icons-material/Campaign";
import ArrowCircleLeftOutlinedIcon from "@mui/icons-material/ArrowCircleLeftOutlined";
import {
  getEmailConversationById,
  saveEmailResponse,
  sendEmailResponse,
} from "../../../services/agentModule/email/email-conversation";
import {
    EMAIL_CONVERSATION_TABS,
  EMAIL_ERRORS,
  EMAIL_MESSAGE,
  EMAIL_STRING,
  EMAIL_SUCCUSS,
} from "../../../common/constants/agentModule/emailContants";
import {
  assignUsers,
  endTextConversation,
} from "../../../services/agentModule/text/text-conversation";
import { useTheme } from "@mui/material/styles";
import RichTextEditor from "../../../components/richtextcomponent";
import AssignModal from "../common/AssignModal";
import EndConversationDailog from "../common/EndConversationDailog";
import { getUsers } from "../../../services/adminModule/users/userServices";
import ProgressOverlay from "../../../common/components/ProgressOverlay";
import { ALIGN, BUTTON_COLORS, DISPLAY, SIZES, STATUS, TYPOGRAPHY_VARIANT, VARIANTS } from "../../../common/constants/commonConstants";
import { phoneNumber } from "../../../utils/utils";

const EditEmailConversation = () => {
  const { customerdataID } = useParams();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [emailContents, setEmailContents] = useState({});
  const [openAssignModal, setOpenAssignModal] = useState(false);
  const [openEndConversationDailog, setOpenEndConversationDailog] =
    useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [emailSubjects, setEmailSubjects] = useState({});
  const [tabValue, setTabValue] = useState(0);
  const theme = useTheme();

  useEffect(() => {
    const fetchCustomerData = async () => {
      try {
        setIsLoading(true);
        const fetchedCustomers = await getEmailConversationById(customerdataID);
        const selectedCustomer = fetchedCustomers.find(
          (cust) => cust.customerdataid === parseInt(customerdataID, 10)
        );

        if (selectedCustomer) {
          setCustomer(selectedCustomer);
          const initialEmails = {};
          const initialSubjects = {};

          selectedCustomer.useremail.forEach((email) => {
            initialEmails[email.emailconvid] = email.cararesponse || "";
            initialSubjects[email.emailconvid] =
              email.cararesponsesubject || "";
          });

          setEmailContents(initialEmails);
          setEmailSubjects(initialSubjects);
        }
      } catch (error) {
        console.error(EMAIL_ERRORS.FETCH_EMAIL_CONVERSATION_BY_ID_ERROR, error);
      } finally {
        setIsLoading(false);
      }
    };

    const fetchUserDetails = async () => {
      try {
        const users = await getUsers();
        if (users) setUsers(users);
      } catch (error) {
        console.error(EMAIL_ERRORS.FETCH_USER_INFO_ERROR, error);
      }
    };
    fetchUserDetails();

    fetchCustomerData();
  }, [customerdataID]);

  const handleSave = async (emailconvid) => {
    const content = emailContents[emailconvid];
    const subject = emailSubjects[emailconvid];

    if (!emailconvid || !content || !subject) return;

    try {
      setIsLoading(true);
      const userguid = localStorage.getItem("userguid");
      await saveEmailResponse({ emailconvid, content, subject, userguid });
    } catch (error) {
      console.error(EMAIL_ERRORS.SAVE_EMAIL_RESPONSE_ERROR, error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendEmail = async (emailconvid) => {
    const content = emailContents[emailconvid];
    const subject = emailSubjects[emailconvid];

    if (!emailconvid || !content || !subject) return;

    try {
      setIsLoading(true);
      const userguid = localStorage.getItem("userguid");
      await sendEmailResponse({ emailconvid, content, subject, userguid });
    } catch (error) {
      console.error(EMAIL_ERRORS.SEND_EMAIL_RESPONSE_ERROR, error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEndConversationClick = () => {
    setOpenEndConversationDailog(true);
  };

  const handleUpdateAssignee = async (id, newAssignee) => {
    try {
      setIsLoading(true);
      await assignUsers(id, newAssignee);
      setOpenAssignModal(false);
      setSuccessMessage(EMAIL_SUCCUSS.ASSIGNEE_UPDATE);
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error(EMAIL_ERRORS.UPDATE_ASSIGNEE_ERROR, error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmEndConversation = async () => {
    try {
      setOpenEndConversationDailog(false);
      setIsLoading(true);
      await endTextConversation(customerdataID);
      setSuccessMessage(EMAIL_SUCCUSS.END_CONVERSATION);
      setTimeout(() => setSuccessMessage(""), 3000);
      navigate(-1);
    } catch (error) {
      console.error(EMAIL_SUCCUSS.END_CONVERSATION_ERROR, error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container maxWidth={false} disableGutters>
      {successMessage && (
        <Alert severity={STATUS.SUCCESS} sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}
      {isLoading && <ProgressOverlay />}
      <Grid container spacing={2} sx={{ mb: 2, alignItems: ALIGN.CENTER }}>
        <Grid size={SIZES.AUTO}>
          <IconButton color={BUTTON_COLORS.SECONDARY} onClick={() => navigate(-1)}>
            <ArrowCircleLeftOutlinedIcon />
          </IconButton>
        </Grid>
        {customer && (
          <>
            <Grid size={SIZES.AUTO}>
              <Typography variant={TYPOGRAPHY_VARIANT.H6}>
                {customer.firstname} {customer.lastname}
              </Typography>
            </Grid>
            <Grid size={SIZES.GROW}>
              <Chip
                icon={<LocalPhoneIcon />}
                label={phoneNumber(customer.phonenumber)}
                size={SIZES.SMALL}
                sx={{ mx: 1 }}
              />
              <Chip
                icon={<EmailIcon />}
                label={customer.emailaddress}
                size={SIZES.SMALL}
              />
              <Chip
                icon={<CampaignIcon />}
                label={customer.campaignname}
                size={SIZES.SMALL}
                sx={{ mx: 1 }}
              />
            </Grid>
            <Box sx={{ ml: "auto", display: DISPLAY.FLEX, gap: 2 }}>
              {customer.convend === 0 && (
                <>
                  <Button
                    variant={VARIANTS.OUTLINED}
                    color={BUTTON_COLORS.OUTLINED}
                    size={SIZES.SMALL}
                    onClick={() => setOpenAssignModal(true)}
                  >
                    {EMAIL_STRING.ASSIGN}
                  </Button>
                  <Button
                    variant={VARIANTS.OUTLINED}
                    color={BUTTON_COLORS.OUTLINED}
                    size={SIZES.SMALL}
                    onClick={() =>
                      handleEndConversationClick(
                        customer.chatData?.[0]?.customerdataid
                      )
                    }
                  >
                    {EMAIL_STRING.END_CONVERSATION}
                  </Button>
                </>
              )}
            </Box>
          </>
        )}
      </Grid>

      <Tabs
        value={tabValue}
        onChange={(e, newValue) => setTabValue(newValue)}
        sx={{ mb: 2 }}
      >
        <Tab label={EMAIL_CONVERSATION_TABS.CONVERSATION} />
        <Tab label={EMAIL_CONVERSATION_TABS.PROFILE} />
        <Tab label={EMAIL_CONVERSATION_TABS.NOTES} />
        <Tab label={EMAIL_CONVERSATION_TABS.PAYMENT} />
        <Tab label={EMAIL_CONVERSATION_TABS.HISTORY} />
        <Tab label={EMAIL_CONVERSATION_TABS.CHATS} />
        <Tab label={EMAIL_CONVERSATION_TABS.CALLS} />
        <Tab label={EMAIL_CONVERSATION_TABS.TEXT} />
      </Tabs>

      <Box sx={{ display: DISPLAY.FLEX, height: "70vh" }}>
        {/* Conversation panel */}
        <Box
          sx={{
            width: tabValue === 0 ? "100%" : "50%",
            pr: tabValue === 0 ? 0 : 2,
            overflowY: "auto",
          }}
        >
          {customer && customer.useremail?.length > 0 ? (
            customer.useremail.map((email, index) => (
              <Accordion key={index}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>{email.userresponsesubject}</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ backgroundColor: "#FAFAFA" }}>
                  <Box className="ai-chatbox">
                    <Box
                      className="ai-chatbox__message ai-chatbox__message--input"
                      sx={{
                        borderLeftColor: theme.palette.primary.main + "60",
                      }}
                    >
                      <Box
                        className="ai-chatbox__message-content"
                        dangerouslySetInnerHTML={{ __html: email.userresponse }}
                      />
                      <Box className="ai-chatbox__message-footer">
                        <span>
                          <b>User</b> on{" "}
                          {new Date(email.userresponsetime).toLocaleString(
                            "en-GB",
                            {
                              weekday: "long",
                              day: "numeric",
                              month: "long",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                              second: "2-digit",
                              hour12: true,
                            }
                          )}
                        </span>
                      </Box>
                    </Box>
                    <Box
                      className="ai-chatbox__message ai-chatbox__message--response"
                      sx={{
                        borderColor:
                          email.cararesponseapproved === 0
                            ? theme.palette.warning.main
                            : theme.palette.primary.main + "30",
                      }}
                    >
                      <Box className="ai-chatbox__message-content">
                        {/* Editable CARA Response Subject */}
                        <Grid
                          container
                          spacing={2}
                          disableGutters
                          sx={{ my: 1, alignItems: "center" }}
                        >
                          <Grid size={SIZES.GROW}>
                            <TextField
                              fullWidth
                              label={EMAIL_STRING.SUBJECT}
                              value={emailSubjects[email.emailconvid] || ""}
                              size={SIZES.SMALL}
                              onChange={(e) =>
                                setEmailSubjects((prev) => ({
                                  ...prev,
                                  [email.emailconvid]: e.target.value,
                                }))
                              }
                              inputProps={{
                                readOnly: email.cararesponseapproved === 1,
                              }}
                            />
                          </Grid>
                          <Grid size={SIZES.AUTO}>
                            <Button
                              variant={VARIANTS.OUTLINED}
                              color={BUTTON_COLORS.PRIMARY}
                              onClick={() => handleSave(email.emailconvid)}
                              disabled={email.cararesponseapproved === 1}
                              startIcon={<Save />}
                              sx={{ minWidth: "100px" }}
                            >
                              {EMAIL_STRING.SAVE}
                            </Button>
                          </Grid>
                          <Grid size={SIZES.AUTO}>
                            <Button
                              variant={VARIANTS.CONTAINED}
                              color={BUTTON_COLORS.WARNING}
                              endIcon={<Send />}
                              onClick={() => handleSendEmail(email.emailconvid)}
                              disabled={email.cararesponseapproved === 1}
                              sx={{ minWidth: "100px" }}
                            >
                              {EMAIL_STRING.SEND_EMAIL}
                            </Button>
                          </Grid>
                        </Grid>

                        {/* RichText Editor for the CARA Response */}
                        <RichTextEditor
                          value={emailContents[email.emailconvid] || ""}
                          onChange={(newContent) =>
                            setEmailContents((prev) => ({
                              ...prev,
                              [email.emailconvid]: newContent,
                            }))
                          }
                          disabled={email.cararesponseapproved === 1}
                          sx={{
                            mr: 2,
                            backgroundColor:
                              email.cararesponseapproved === 1
                                ? "gray"
                                : undefined,
                          }}
                        />
                      </Box>
                      <Box className="ai-chatbox__message-footer">
                        <span>
                          <b>Cara</b> on{" "}
                          {new Date(email.cararesponsetime).toLocaleString(
                            "en-GB",
                            {
                              weekday: "long",
                              day: "numeric",
                              month: "long",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                              second: "2-digit",
                              hour12: true,
                            }
                          )}
                        </span>
                      </Box>
                    </Box>
                  </Box>
                  <Box sx={{ mt: 2 }}></Box>
                </AccordionDetails>
              </Accordion>
            ))
          ) : (
            <Typography>{EMAIL_MESSAGE.NO_EMAIL_CONVERSATION}</Typography>
          )}
        </Box>

        {/* Right side panel for other tabs when selected */}
        {tabValue !== 0 && (
          <Box sx={{ width: "50%", pl: 2, overflowY: "auto" }}>
            {tabValue === 1 && <Typography>Profile placeholder</Typography>}
            {tabValue === 2 && <Typography>Notes placeholder</Typography>}
            {tabValue === 3 && <Typography>Payment placeholder</Typography>}
            {tabValue === 4 && <Typography>History placeholder</Typography>}
            {tabValue === 5 && <Typography>Chats placeholder</Typography>}
            {tabValue === 6 && <Typography>Calls placeholder</Typography>}
            {tabValue === 7 && <Typography>Texts placeholder</Typography>}
          </Box>
        )}
      </Box>

      {openAssignModal && (
        <AssignModal
          open={openAssignModal}
          customerId={customerdataID}
          users={users}
          assignedCust={customer.assignedto}
          onAssign={handleUpdateAssignee}
          onClose={() => setOpenAssignModal(false)}
        />
      )}

      <EndConversationDailog
        open={openEndConversationDailog}
        onClose={() => setOpenEndConversationDailog(false)}
        onConfirm={handleConfirmEndConversation}
      />
    </Container>
  );
};

export default EditEmailConversation;
